import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { useTranslation } from 'react-i18next';
import { detailToMessage } from '../lib/apiError';
import { isValidEmail } from '../lib/validation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setError(t('auth.invalid_email'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: trimmedEmail });
      setSent(true);
    } catch (err) {
      setError(detailToMessage(err.response?.data?.detail) || t('auth.something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex">
      <div className="flex-1 flex flex-col items-center px-8 py-12">
        <div className="w-full max-w-md my-auto">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BusyBeeLogo className="h-10" />
          </Link>

          <h1 className="text-3xl font-bold text-white mb-2">{t('auth.forgot_password')}</h1>
          <p className="text-gray-400 mb-8">
            {t('auth.reset_password_subtitle')}
          </p>

          {sent ? (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-green-400 text-sm font-medium">{t('auth.check_inbox')}</p>
                <p className="text-green-400/80 text-sm mt-1">
                  {t('auth.check_inbox_message', { email })}
                </p>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-gray-300">{t('auth.email_label')}</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                      placeholder={t('auth.email_placeholder')}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-500 text-black hover:bg-amber-400 h-12"
                  disabled={loading}
                >
                  {loading ? t('auth.sending') : t('auth.send_reset_link')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </>
          )}

          <p className="mt-8 text-center text-gray-400">
            <Link to="/login" className="text-amber-400 hover:text-amber-300">
              {t('auth.back_to_sign_in')}
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-l border-gray-800">
        <div className="text-center px-12">
          <h2 className="text-4xl font-bold text-white mb-4">{t('auth.hero_title')}</h2>
          <p className="text-xl text-gray-400">{t('auth.hero_subtitle')}</p>
        </div>
      </div>
    </div>
  );
}
